import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AxiosInterceptor } from "./axios_call";
import "./scss/style.scss";
import AuthenticatedRoute from "./route/AuthenticatedRoute";
import UnAuthenticatedRoute from "./route/UnAuthenticatedRoute";
import axios from "axios";
import { BASE_URL } from "./components/constant_api";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppLoader from "./components/loaders/AppLoader";
import { useGetRoleListQuery } from "./store/querys/userApi";
// TODO: apne project ka sahi path daalo
 
// ---- Lazy loaded pages (each becomes its own chunk) ----
const DefaultLayout = lazy(() => import("./hoc/Layout/Layout"));
const Login = lazy(() => import("./components/auth/Login"));
const Logout = lazy(() => import("./components/auth/Logout"));
const OTP = lazy(() => import("./components/auth/OtpScreen"));
const ChangePassword = lazy(() => import("./components/auth/ChangePassword"));
const ResetPassword = lazy(() => import("./components/auth/ResetPassword"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const VerifyEmail = lazy(() => import("./components/auth/VerifyEmail"));
const RegisterNew = lazy(() => import("./components/auth/RegisterNew"));
const ApprovUser = lazy(() => import("./components/auth/ApprovUser"));
const SetPassword = lazy(() => import("./components/auth/SetPassword"));
const ConfirmOrganisationInvite = lazy(
  () => import("./components/auth/ConfirmInvitation"),
);
const XeroCredentialsGuide = lazy(
  () => import("./containers/Xerocredentialsguide/Xerocredentialsguide"),
);
const SelectBankAccountModal = lazy(
  () => import("./components/Selectbankaccountmodal"),
);

/* ---------------- Route permission config ---------------- */
// Permission na hone pe user yahan redirect hoga
const OPEN_QUERY_PATH = "/query";

// path   -> route prefix
// module -> ModulePermissions ka key (role.ModulePermissions[module].enabled)
// field  -> (optional) details.fields[field] bhi allowed hona chahiye
const ROUTE_RULES = [
  { path: "/account-transactions", module: "transaction" },
  { path: "/bank-statements", module: "bank-statement" },
  { path: "/bank-feeds", module: "bank-feed" },
  { path: "/bills", module: "bill" },
];

// true ya ["view"] / ["edit"] / ["view","edit"] => allowed
// false, undefined, [] ya ["hide"] => not allowed
const isAllowedValue = (value) =>
  value === true ||
  (Array.isArray(value) && (value.includes("view") || value.includes("edit")));

const hasRouteAccess = (role, rule) => {
  const moduleConfig = role?.ModulePermissions?.[rule.module];
  if (!moduleConfig || moduleConfig.enabled !== true) return false;
  if (rule.field) {
    return isAllowedValue(moduleConfig.details?.fields?.[rule.field]);
  }
  return true;
};

function App() {
  const navigate = useNavigate();
  const [userDetail, setUserDetail] = useState(() =>
    JSON.parse(localStorage.getItem("userDetail")),
  );
  const [userDetailLoaded, setUserDetailLoaded] = useState(false);
  const currentUser = userDetail;
  const currentUserId = currentUser?.UserID;
  const location = useLocation();
  const {
    loading: authLoading,
    error: authError,
    isAuthenticated,
  } = useSelector((state) => ({
    loading: state.auth.loading,
    error: state.auth.error,
    isAuthenticated: state.auth?._id !== null,
  }));

  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [organisationId, setOrganisationId] = useState(
    localStorage.getItem("OrganisationID") || "",
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isNew = params.get("isNew");

    if (isNew === "true") {
      handleNewOrganisationRedirect("");
    }
  }, [location.search, currentUserId]);

  useEffect(() => {
    if (organisationId) {
      handleLoggedinUserDetails();
    }
  }, [organisationId]);

  /* ---------------- Role based route guard ---------------- */
  const { data: roleListData, isFetching: roleFetching } =
    useGetRoleListQuery(
      { OrganisationID: organisationId, UserId: currentUserId },
      {
        skip: !organisationId || !currentUserId,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
      },
    );

  const currentRole = useMemo(() => {
    const roles = Array.isArray(roleListData)
      ? roleListData
      : roleListData?.data || [];
    const roleId = userDetail?.Role?.RoleID;
    const roleName = userDetail?.Role?.Name;
    const roleSlug = userDetail?.Role?.Slug;

    // RoleID sabse reliable hai (Admin aur "Admin (copy)" ka Slug same hai)
    return (
      roles.find((r) => roleId && r.RoleID === roleId) ||
      roles.find((r) => roleName && r.Name === roleName) ||
      roles.find((r) => roleSlug && r.Slug === roleSlug) ||
      null
    );
  }, [roleListData, userDetail]);

  useEffect(() => {
    // Fresh user details + role list aane tak wait karo
    if (!userDetailLoaded || roleFetching) return;

    if (!currentRole) {
      // Debug: role match nahi hua to guard kuch nahi karega
      console.warn("[RouteGuard] Current role not found in role list", {
        userRole: userDetail?.Role,
      });
      return;
    }

    const pathname = location.pathname;

    // Open Query pe already ho to loop mat banao
    if (
      pathname === OPEN_QUERY_PATH ||
      pathname.startsWith(`${OPEN_QUERY_PATH}/`)
    )
      return;

    const rule = ROUTE_RULES.find(
      (r) => pathname === r.path || pathname.startsWith(`${r.path}/`),
    );

    // Jo route rules me nahi hai usko guard nahi karte
    if (!rule) return;

    const allowed = hasRouteAccess(currentRole, rule);
    console.log("[RouteGuard]", {
      pathname,
      role: currentRole?.Name,
      rule,
      allowed,
    });

    if (!allowed) {
      navigate(OPEN_QUERY_PATH, { replace: true });
    }
  }, [
    location.pathname,
    currentRole,
    roleFetching,
    userDetailLoaded,
    userDetail,
    navigate,
  ]);

  const handleNewOrganisationRedirect = async (organisationIdFromUrl) => {
    try {
      const response = await axios.get(`${BASE_URL}/user/${currentUserId}`);

      const userData = response?.data?.data;

      if (userData && typeof userData === "object") {
        // Store complete user object
        localStorage.setItem("userDetail", JSON.stringify(userData));
        setUserDetail(userData);
        // Store UserID
        if (userData?.UserID) {
          localStorage.setItem(
            "currentLoginUserId",
            JSON.stringify(userData.UserID),
          );
        }

        setShowBankAccountModal(true);
      }
    } catch (error) {
      console.error("Error handling new organisation redirect:", error);
    }
  };

  const handleLoggedinUserDetails = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/user/details/${currentUserId}?OrganisationID=${organisationId}`,
      );

      const userData = response?.data?.data;

      if (userData && typeof userData === "object") {
        // Store complete user object
        localStorage.setItem("userDetail", JSON.stringify(userData));
        setUserDetail(userData);
        // Store UserID
        if (userData?.UserID) {
          localStorage.setItem(
            "currentLoginUserId",
            JSON.stringify(userData.UserID),
          );
        }
      }
    } catch (error) {
      console.error("Error handling loggedin user details:", error);
    } finally {
      setUserDetailLoaded(true);
    }
  };

  const handleBankAccountSubmit = async (
    selectedAccountIds,
    selectedUserIds,
    submitResponse,
  ) => {};

  return (
    <Suspense fallback={<AppLoader />}>
      <AxiosInterceptor>
        <Routes>
          <Route
            exact
            path="/login"
            name="Login Page"
            element={
              <AuthenticatedRoute redirectTo="/query">
                <Login />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact
            path="/forgotpassword"
            name="ForgotPassword Page"
            element={<ForgotPassword />}
          />
          <Route exact path="/otp" name="OTP Page" element={<OTP />} />
          <Route
            exact
            path="/changepassword"
            name="ChangePassword Page"
            element={<ChangePassword />}
          />

          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<RegisterNew />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/approve-user" element={<ApprovUser />} />
          <Route
            path="/confirm-organisation-invite"
            element={<ConfirmOrganisationInvite />}
          />
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/xero-credentials-guide"
            element={<XeroCredentialsGuide />}
          />
          <Route
            path="*"
            name="Home"
            element={
              <UnAuthenticatedRoute redirectTo="/login">
                <DefaultLayout />
              </UnAuthenticatedRoute>
            }
          />
        </Routes>
      </AxiosInterceptor>
      <ToastContainer />
      {/* Only load the modal chunk when it is actually needed */}
      {showBankAccountModal && (
        <SelectBankAccountModal
          isOpen={showBankAccountModal}
          organisationId={organisationId}
          currentUserId={currentUserId}
          onClose={() => setShowBankAccountModal(false)}
          onSubmit={handleBankAccountSubmit}
        />
      )}
    </Suspense>
  );
}

export default App;